import { Button } from 'components/ui/button';
import { ComponentProps, ReactNode } from 'react';
import { GLYPH_OWN_SIZE } from 'components/buttons/buttonStyle';
import { NEW_FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { cn } from 'utils/cn';

const SIZE = { small: 'icon-sm', medium: 'icon', large: 'icon-lg' } as const;
const FONT_SIZE = { small: 'text-xs', medium: 'text-base', large: 'text-xl' } as const;
/**
 * A 20px content box at every size (16px when a caller shrinks a medium button to 32px), with `border-0`
 * so the kit's transparent border does not take another pixel. The kit pins every svg to `shrink-0`;
 * `SVG_SHRINKS` lets an svg that does not ask for that itself (the vendored glyphs do) shrink into the box,
 * so an svg asset drawn at `size-5` in a 32px button renders 16px wide.
 *
 * With no border, the kit button's focus style is its 50% ring alone, under 3:1 on white: the new design turns it
 * off (`KIT_RING_OFF`) and draws its solid ring outside the button in its place. Classic keeps the kit's.
 */
const PADDING = { small: 'p-1.5', medium: 'p-2', large: 'p-2.5' } as const;
const SVG_SHRINKS = '[&_svg:not(.shrink-0)]:shrink';
const KIT_RING_OFF = 'new:focus-visible:ring-0';

/** Fill per variant and colour. `text` is the bare glyph, `light` a tint, `contained` a solid fill. */
const COLOR = {
    text: {
        primary: 'text-primary hover:bg-accent hover:text-primary',
        info: 'text-info hover:bg-info-lighter hover:text-info',
        secondary: 'text-secondary hover:bg-warning-light hover:text-secondary',
    },
    light: {
        primary: 'bg-accent text-primary hover:bg-sidebar-accent hover:text-primary disabled:bg-grey-200',
        info: 'bg-info-lighter text-info hover:bg-info-lighter hover:text-info disabled:bg-grey-200',
        secondary: 'bg-warning-light text-secondary hover:bg-warning-light hover:text-secondary disabled:bg-grey-200',
    },
    contained: {
        primary:
            'bg-primary text-primary-foreground hover:bg-primary/80 hover:text-primary-foreground disabled:bg-grey-200',
        info: 'bg-info text-primary-foreground hover:bg-info/80 hover:text-primary-foreground disabled:bg-grey-200',
        secondary:
            'bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:text-secondary-foreground disabled:bg-grey-200',
    },
} as const;

export interface Props extends Omit<ComponentProps<typeof Button>, 'variant' | 'size' | 'color' | 'className'> {
    variant?: keyof typeof COLOR;
    color?: keyof (typeof COLOR)['text'];
    shape?: 'rounded' | 'square';
    size?: keyof typeof SIZE;
    /** Pulls the button into the padding of the edge it sits on, so the glyph lines up with the text. */
    edge?: 'start' | 'end';
    className?: string;
    children: ReactNode;
}

export default function IconButton({
    variant = 'text',
    shape = 'square',
    color = 'primary',
    size = 'medium',
    edge,
    className,
    children,
    ...others
}: Props) {
    return (
        <Button
            variant="ghost"
            size={SIZE[size]}
            className={cn(
                'relative border-0',
                NEW_FOCUS_RING,
                KIT_RING_OFF,
                FONT_SIZE[size],
                PADDING[size],
                GLYPH_OWN_SIZE,
                SVG_SHRINKS,
                COLOR[variant][color],
                shape === 'rounded' ? 'rounded-full' : 'rounded-sm',
                edge === 'start' && '-ms-3',
                edge === 'end' && '-me-3',
                className
            )}
            {...others}
        >
            {children}
        </Button>
    );
}
